import { MapCanvasShapesContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasShapes";
import { MapEditorPropsContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorProps";
import {
  EMPTY_ZONE_CHECKS,
  type ZoneChecks,
  ZoneChecksWatcher
} from "@gettactic/map-builder/src/components/map/hooks/useZoneChecks";
import { computeWallFloorPolygons } from "@gettactic/map-builder/src/utils/wallFloorFill";
import { getAllWallSegments } from "@gettactic/map-builder/src/utils/wallPathSegments";
import React from "react";
import { type RoomPolygon, unionRoomPolygons } from "../document/roomPolygons";
import { orderedShapes } from "../document/serialize";
import { useEditorSelector } from "../store/EditorStoreContext";
import { useActiveTool, useEditorUi, useViewport } from "../store/selectors";

const CONFLICT_COLOR = "#dc2626";
const UNTAGGED_COLOR = "#d97706";
const UNPLACED_COLOR = "#dc2626";
/** Gap between the dot's edge and the ring around it. */
const UNPLACED_MARGIN = 6;

/** Zone checks drawn on the canvas: overlapping regions red, untagged open
 *  areas amber, desks in no space ringed. The watcher settles the checks
 *  after edits stop, so dragging never recomputes containment per frame. */
export const ZoneChecksSvg: React.FC = () => {
  const { showZoneChecks } = useEditorUi();
  const [checks, setChecks] = React.useState<ZoneChecks>(EMPTY_ZONE_CHECKS);
  // The watcher reads the legacy shapes context, which only the editor
  // chrome provides; a bare canvas draws no checks.
  const hasLegacyShapes = React.useContext(MapCanvasShapesContext) !== null;
  if (!showZoneChecks || !hasLegacyShapes) return null;
  return (
    <g data-layer="zone-checks" pointerEvents="none">
      <ZoneChecksWatcher onChange={setChecks} />
      {checks.untagged.map((region) => (
        <rect
          key={region.id}
          x={region.x - 3}
          y={region.y - 3}
          width={region.width + 6}
          height={region.height + 6}
          fill="none"
          stroke={UNTAGGED_COLOR}
          strokeWidth={2}
          strokeDasharray="6 4"
        />
      ))}
      {checks.conflicting.map((region) => (
        <rect
          key={region.id}
          x={region.x - 4}
          y={region.y - 4}
          width={region.width + 8}
          height={region.height + 8}
          fill="none"
          stroke={CONFLICT_COLOR}
          strokeWidth={3}
          strokeDasharray="10 5"
        />
      ))}
      {checks.unplaced.map((spot) =>
        spot.hidesDot && spot.areaBounds ? (
          // No dot to ring: the highlight area is the spot's indicator.
          <rect
            key={spot.id}
            data-unplaced-area={spot.id}
            x={spot.areaBounds.x - UNPLACED_MARGIN}
            y={spot.areaBounds.y - UNPLACED_MARGIN}
            width={spot.areaBounds.width + UNPLACED_MARGIN * 2}
            height={spot.areaBounds.height + UNPLACED_MARGIN * 2}
            rx={UNPLACED_MARGIN}
            fill="none"
            stroke={UNPLACED_COLOR}
            strokeWidth={2.5}
            strokeDasharray="6 4"
          />
        ) : (
          <circle
            key={spot.id}
            data-unplaced={spot.id}
            cx={spot.dotX}
            cy={spot.dotY}
            r={spot.dotRadius + UNPLACED_MARGIN}
            fill="none"
            stroke={UNPLACED_COLOR}
            strokeWidth={2.5}
            strokeDasharray="6 4"
          />
        )
      )}
    </g>
  );
};

const GUIDE_POINT_COLOR = "#9333ea";
const GUIDE_POINT_RADIUS = 8;

/** Read-only marker for the host's guide point (e.g. a request's pin). */
export const GuidePointSvg: React.FC = () => {
  const guidePoint = React.useContext(MapEditorPropsContext)?.guidePoint;
  if (!guidePoint) return null;
  return (
    <g data-layer="guide-point" pointerEvents="none">
      <circle
        cx={guidePoint.x}
        cy={guidePoint.y}
        r={GUIDE_POINT_RADIUS * 2.5}
        fill={GUIDE_POINT_COLOR}
        opacity={0.15}
      />
      <circle
        cx={guidePoint.x}
        cy={guidePoint.y}
        r={GUIDE_POINT_RADIUS}
        fill={GUIDE_POINT_COLOR}
        stroke="#ffffff"
        strokeWidth={2}
      />
    </g>
  );
};

/** Rooms detected from the wall graph while the auto-zone tool is active;
 *  the tool turns the clicked one (`data-room-index`) into a zone. */
export const AutoZoneSvg: React.FC = () => {
  const { kind } = useActiveTool();
  const shapesById = useEditorSelector((state) => state.shapes);
  const order = useEditorSelector((state) => state.order);
  const picks = useEditorSelector((state) => state.chrome.autoZonePicks);
  const { scale } = useViewport();
  const polygons = React.useMemo(() => {
    if (kind !== "auto-zone") return [];
    return computeWallFloorPolygons(
      getAllWallSegments(orderedShapes({ shapes: shapesById, order }))
    );
  }, [kind, shapesById, order]);
  // Picked rooms take their zone's colour; a merged pick also gets one
  // outline around all its rooms and a number at its centre.
  const colorOf = (position: number) =>
    picks[position]?.tag?.color || "#16a34a";
  const groupOfRoom = new Map<number, number>();
  picks.forEach((pick, position) => {
    for (const room of pick.rooms) groupOfRoom.set(room, position);
  });
  const outlines = picks.map((pick) =>
    unionRoomPolygons(
      pick.rooms
        .map((room) => polygons[room])
        .filter((room): room is RoomPolygon => !!room)
    )
  );
  if (kind !== "auto-zone") return null;
  return (
    <g data-layer="auto-zone">
      {polygons.map((polygon, index) => {
        const group = groupOfRoom.get(index);
        const isPicked = group !== undefined;
        const pickColor = group !== undefined ? colorOf(group) : "#2563eb";
        return (
          <polygon
            // biome-ignore lint/suspicious/noArrayIndexKey: index matches the tool's own recomputation
            key={index}
            data-room-index={index}
            data-picked-group={group}
            points={polygon.points
              .map((point) => `${point.x},${point.y}`)
              .join(" ")}
            fill={pickColor}
            fillOpacity={isPicked ? 0.35 : 0.18}
            stroke={pickColor}
            strokeWidth={2}
            strokeDasharray={isPicked ? undefined : "8 4"}
            className={
              isPicked
                ? "cursor-pointer"
                : "cursor-pointer hover:fill-blue-500/30"
            }
          />
        );
      })}
      {outlines.map((outline, position) => {
        if (!outline) return null;
        const centre = polygonCentre(outline);
        const pickColor = colorOf(position);
        return (
          <g
            // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional
            key={position}
            pointerEvents="none"
          >
            <polygon
              points={outline.map((point) => `${point.x},${point.y}`).join(" ")}
              fill="none"
              stroke={pickColor}
              strokeWidth={3}
              vectorEffect="non-scaling-stroke"
            />
            <g
              transform={`translate(${centre.x} ${centre.y}) scale(${1 / scale})`}
            >
              <circle r={11} fill={pickColor} />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontSize={12}
                fontWeight={700}
                fontFamily="Quicksand"
              >
                {position + 1}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
};

const polygonCentre = (points: { x: number; y: number }[]) => ({
  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
  y: points.reduce((sum, point) => sum + point.y, 0) / points.length
});
